import * as THREE from 'three';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';

/**
 * Screen-space radial / zoom blur with optional per-channel chromatic split.
 *
 * Each pixel samples the source buffer along the vector from a configurable
 * vanishing-point center toward itself. At the center: zero smear. At the
 * edges: smear length = strength * radius^falloff. Per-channel offsets give
 * the streaks a sci-fi color separation when chromaticAmount > 0.
 *
 * Drive `setStrength()` per frame from speed; drive `setCenter()` from the
 * projected camera-forward vector so the blur tracks where you're looking.
 *
 * Cost order: ~1.5 ms at samples=8 on a mid-Adreno phone. setStrength(0)
 * disables the pass entirely so stationary/title frames do not pay for an
 * otherwise invisible fullscreen blit.
 */
export interface RadialBlurOptions {
  samples?: 4 | 8 | 16;
  strength?: number;
  centerX?: number;
  centerY?: number;
  /** Power on radial distance. 1 = linear smear, 2+ = sharper focal point. */
  falloff?: number;
  /** 0..~0.02. Per-channel UV offset for chromatic speed-streaks. */
  chromaticAmount?: number;
}

function buildShader(samples: number) {
  return {
    uniforms: {
      tDiffuse: { value: null as THREE.Texture | null },
      uStrength: { value: 0 },
      uCenter: { value: new THREE.Vector2(0.5, 0.5) },
      uFalloff: { value: 1.6 },
      uChromatic: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      #define SAMPLES ${samples}
      uniform sampler2D tDiffuse;
      uniform float uStrength;
      uniform vec2 uCenter;
      uniform float uFalloff;
      uniform float uChromatic;
      varying vec2 vUv;

      vec3 sampleSplit(vec2 uv, vec2 dir, float chromatic) {
        // Split RGB along the radial direction: R lags, B leads. At the focal
        // point dir.length() is tiny so chromatic split self-attenuates — no
        // colored fringing on stationary objects.
        float r = texture2D(tDiffuse, uv - dir * chromatic).r;
        float g = texture2D(tDiffuse, uv).g;
        float b = texture2D(tDiffuse, uv + dir * chromatic).b;
        return vec3(r, g, b);
      }

      void main() {
        if (uStrength < 0.001) {
          gl_FragColor = texture2D(tDiffuse, vUv);
          return;
        }
        vec2 dir = vUv - uCenter;
        float radial = length(dir);
        float curved = pow(radial, uFalloff);
        float weight = uStrength * curved;
        vec3 acc = vec3(0.0);
        float wSum = 0.0;
        for (int i = 0; i < SAMPLES; i++) {
          float t = float(i) / float(SAMPLES - 1); // 0..1
          vec2 offset = dir * (-weight) * t;
          float w = 1.0 - t * 0.5;
          vec2 sampleUv = vUv + offset;
          acc += sampleSplit(sampleUv, dir, uChromatic) * w;
          wSum += w;
        }
        gl_FragColor = vec4(acc / wSum, 1.0);
      }
    `,
  };
}

export class RadialBlurPass extends ShaderPass {
  private samples: 4 | 8 | 16;

  constructor(opts: RadialBlurOptions = {}) {
    const samples = opts.samples ?? 8;
    super(buildShader(samples));
    this.samples = samples;
    this.setStrength(opts.strength ?? 0);
    this.setCenter(opts.centerX ?? 0.5, opts.centerY ?? 0.5);
    this.setFalloff(opts.falloff ?? 1.6);
    this.setChromatic(opts.chromaticAmount ?? 0);
  }

  setStrength(value: number): void {
    const strength = Math.max(0, value);
    (this.uniforms.uStrength as THREE.IUniform<number>).value = strength;
    this.enabled = strength >= 0.001;
  }

  setCenter(x: number, y: number): void {
    (this.uniforms.uCenter as THREE.IUniform<THREE.Vector2>).value.set(x, y);
  }

  setFalloff(value: number): void {
    (this.uniforms.uFalloff as THREE.IUniform<number>).value = Math.max(0.2, value);
  }

  setChromatic(value: number): void {
    (this.uniforms.uChromatic as THREE.IUniform<number>).value = Math.max(0, value);
  }

  setSamples(samples: 4 | 8 | 16): boolean {
    if (samples === this.samples) return false;
    const prev = {
      strength: (this.uniforms.uStrength as THREE.IUniform<number>).value,
      center: (this.uniforms.uCenter as THREE.IUniform<THREE.Vector2>).value.clone(),
      falloff: (this.uniforms.uFalloff as THREE.IUniform<number>).value,
      chromatic: (this.uniforms.uChromatic as THREE.IUniform<number>).value,
    };
    const fresh = buildShader(samples);
    this.material.dispose();
    this.material = new THREE.ShaderMaterial({
      uniforms: fresh.uniforms,
      vertexShader: fresh.vertexShader,
      fragmentShader: fresh.fragmentShader,
    });
    this.uniforms = (this.material as THREE.ShaderMaterial).uniforms;
    this.setStrength(prev.strength);
    (this.uniforms.uCenter as THREE.IUniform<THREE.Vector2>).value.copy(prev.center);
    this.setFalloff(prev.falloff);
    this.setChromatic(prev.chromatic);
    this.samples = samples;
    return true;
  }
}
